import type { CatalogItem, CategorySlug, RawDvsItem, SelectedFilters } from "./types";

export function mapRawDvsToCatalog(rawDvs: RawDvsItem[]): CatalogItem[] {
  return rawDvs.map((item, index) => {
    if (item.categorySlug) {
      return item as unknown as CatalogItem;
    }

    const fuel =
      item.engineType === "Дизельный"
        ? "Дизель"
        : item.engineType === "Гибридный"
          ? "Гибрид"
          : "Бензин";
    const dispVal = item.volume ? `${item.volume} л` : "2.0 л";
    const cleanPower = item.power || "150 л.с.";
    const code = (item.engineCode || "G4KD").toUpperCase();
    const yearToClean = item.yearTo && item.yearTo < 2026 ? item.yearTo : "н.в.";

    return {
      id: `dvs-${index + 1}`,
      category: "Двигатель в сборе (ДВС)",
      categorySlug: "dvs",
      brand: item.brand || "Unknown",
      brandRu: item.brandRu || "",
      baseModel: item.baseModel || "",
      model: `${item.baseModel || "Model"}${item.generation ? " (" + item.generation + ")" : ""}`,
      modelRu: item.modelRu || "",
      year: `${item.yearFrom || "2000"}–${yearToClean}`,
      fuel,
      displacement: dispVal,
      power: cleanPower,
      engineCode: code,
      transmissionType:
        item.modification?.includes("AMT") || item.modification?.includes("AT")
          ? "АКПП"
          : "МКПП",
      driveType: "Передний",
      oem: `OEM-${code}-${dispVal.replace(" ", "")}`,
      donorVin: `VIN${(item.brand || "XX").substring(0, 2).toUpperCase()}${item.yearFrom || 0}${(index % 90000) + 10000}`,
      origin: ["BMW", "Volkswagen", "Audi", "Skoda"].includes(item.brand)
        ? "Германия"
        : "Южная Корея",
      mileage: `${45 + (index % 40)} 000 км`,
      warrantyDays: 180,
      priceEst: `${95 + (index % 280)} 000 ₽`,
      stockStatus: "В наличии",
      inStockCount: (index % 4) + 1,
      compression:
        item.engineType === "Дизельный"
          ? "24.0 бар (дизель)"
          : "12.2 - 12.8 бар (проверено)",
      hasAttachments: "С навесным оборудованием",
      features: `Контрактный двигатель ${code} (${dispVal}, ${cleanPower}) для ${item.brand} ${item.baseModel}.`,
    };
  });
}

async function parseCatalogPayload(body: unknown): Promise<CatalogItem[]> {
  if (Array.isArray(body)) {
    if (body.length === 0) {
      throw new Error("Каталог пуст");
    }

    const first = body[0] as CatalogItem;
    if (first?.id && first?.engineCode) {
      return body as CatalogItem[];
    }

    return mapRawDvsToCatalog(body as RawDvsItem[]);
  }

  if (body && typeof body === "object") {
    const record = body as { items?: CatalogItem[]; error?: string };
    if (record.items && Array.isArray(record.items) && record.items.length > 0) {
      return record.items;
    }
    throw new Error(record.error || "Некорректный ответ каталога");
  }

  throw new Error("Некорректный формат каталога");
}

async function fetchCatalogFromStatic(): Promise<CatalogItem[]> {
  const response = await fetch("/database_collapsed.json", { cache: "no-store" });

  if (!response.ok) {
    throw new Error(`Статический каталог недоступен (${response.status})`);
  }

  return parseCatalogPayload(await response.json());
}

async function fetchCatalogFromApi(): Promise<CatalogItem[]> {
  const response = await fetch("/api/catalog", { cache: "no-store" });

  if (!response.ok) {
    let message = `Ошибка API каталога (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  return parseCatalogPayload(await response.json());
}

export async function loadDatabase(): Promise<CatalogItem[]> {
  // Сначала API: сервер отдаёт уже mapped-каталог из кэша (быстрее, без тяжёлого map на клиенте)
  try {
    return await fetchCatalogFromApi();
  } catch (apiError) {
    try {
      return await fetchCatalogFromStatic();
    } catch (staticError) {
      const apiMsg =
        apiError instanceof Error ? apiError.message : "ошибка API";
      const staticMsg =
        staticError instanceof Error ? staticError.message : "ошибка static";
      throw new Error(`${apiMsg}. ${staticMsg}`);
    }
  }
}

export function getBrands(db: CatalogItem[], categorySlug: CategorySlug = "all"): string[] {
  let filtered = db;
  if (categorySlug !== "all") {
    filtered = filtered.filter((item) => item.categorySlug === categorySlug);
  }
  return [...new Set(filtered.map((item) => item.brand))].sort();
}

export function getBaseModels(
  db: CatalogItem[],
  brand: string,
  categorySlug: CategorySlug = "all",
): string[] {
  if (!brand) return [];
  let filtered = db.filter((item) => item.brand === brand);
  if (categorySlug !== "all") {
    filtered = filtered.filter((item) => item.categorySlug === categorySlug);
  }
  return [...new Set(filtered.map((item) => item.baseModel || item.model))].sort();
}

export function getFuelTypes(
  db: CatalogItem[],
  brand: string,
  model: string,
  categorySlug: CategorySlug = "all",
): string[] {
  if (!brand || !model) return [];
  let filtered = db.filter(
    (item) => item.brand === brand && (item.model === model || item.baseModel === model),
  );
  if (categorySlug !== "all") {
    filtered = filtered.filter((item) => item.categorySlug === categorySlug);
  }
  return [...new Set(filtered.map((item) => item.fuel))].sort();
}

export function getDisplacements(
  db: CatalogItem[],
  brand: string,
  model: string,
  fuel: string,
  categorySlug: CategorySlug = "all",
): string[] {
  if (!brand || !model || !fuel) return [];
  let filtered = db.filter(
    (item) =>
      item.brand === brand &&
      (item.model === model || item.baseModel === model) &&
      item.fuel === fuel,
  );
  if (categorySlug !== "all") {
    filtered = filtered.filter((item) => item.categorySlug === categorySlug);
  }
  return [...new Set(filtered.map((item) => item.displacement))].sort(
    (a, b) => parseFloat(a) - parseFloat(b),
  );
}

export function getPowers(
  db: CatalogItem[],
  brand: string,
  model: string,
  fuel: string,
  displacement: string,
  categorySlug: CategorySlug = "all",
): string[] {
  if (!brand || !model || !fuel || !displacement) return [];
  let filtered = db.filter(
    (item) =>
      item.brand === brand &&
      (item.model === model || item.baseModel === model) &&
      item.fuel === fuel &&
      item.displacement === displacement,
  );
  if (categorySlug !== "all") {
    filtered = filtered.filter((item) => item.categorySlug === categorySlug);
  }
  return [...new Set(filtered.map((item) => item.power))].sort(
    (a, b) => parseInt(a) - parseInt(b),
  );
}

export function findMatchingEngines(
  db: CatalogItem[],
  filters: SelectedFilters & { categorySlug: CategorySlug },
): CatalogItem[] {
  const { categorySlug, brand, model, fuel, displacement, power, searchQuery } = filters;

  return db.filter((item) => {
    if (categorySlug !== "all" && item.categorySlug !== categorySlug) {
      return false;
    }
    if (brand && item.brand !== brand) return false;
    if (model && item.model !== model && item.baseModel !== model) return false;
    if (fuel && item.fuel !== fuel) return false;
    if (displacement && item.displacement !== displacement) return false;
    if (power && item.power !== power) return false;

    if (searchQuery.trim() !== "") {
      const words = searchQuery.trim().toLowerCase().split(/\s+/);
      const haystack = [
        item.brand,
        item.brandRu || "",
        item.model,
        item.modelRu || "",
        item.baseModel,
        item.engineCode,
        item.transmissionType,
        item.oem,
        item.donorVin,
        item.displacement,
        item.power,
      ]
        .join(" ")
        .toLowerCase();

      return words.every((word) => haystack.includes(word));
    }

    return true;
  });
}

export function buildSearchSuggestions(
  db: CatalogItem[],
  query: string,
): import("./types").SearchSuggestion[] {
  const q = query.trim().toLowerCase();
  if (!q || q.length < 2) return [];

  const suggestions: import("./types").SearchSuggestion[] = [];
  const seen = new Set<string>();

  for (const item of db) {
    if (item.engineCode && item.engineCode.toLowerCase().includes(q)) {
      const text = `${item.engineCode} (${item.categorySlug === "kpp" ? "КПП" : "ДВС"} для ${item.brand} ${item.baseModel})`;
      if (!seen.has(text)) {
        seen.add(text);
        suggestions.push({ type: "code", value: item.engineCode, label: text });
      }
    }

    const modelMatch = `${item.brand} ${item.baseModel}`;
    const modelMatchRu = `${item.brandRu || ""} ${item.modelRu || item.baseModel}`;

    if (modelMatch.toLowerCase().includes(q) || modelMatchRu.toLowerCase().includes(q)) {
      const catLabel =
        item.categorySlug === "kpp"
          ? item.transmissionType || "КПП"
          : "Двигатель (ДВС)";
      const text = `${catLabel} на ${item.brand} ${item.baseModel}`;
      if (!seen.has(text)) {
        seen.add(text);
        suggestions.push({
          type: "model",
          brand: item.brand,
          model: item.baseModel,
          value: `${item.brand} ${item.baseModel}`,
          label: text,
          categorySlug: item.categorySlug,
        });
      }
    }

    if (suggestions.length >= 8) break;
  }

  return suggestions;
}
